import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Row,
  Col,
  Button,
  Radio,
  Tag,
  Modal,
  message,
  Spin,
  Alert,
  Table,
  Tabs,
  Divider,
} from 'antd';
import {
  WalletOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  AlipayOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  getRechargePackages,
  createRechargeOrder,
  getRechargeOrders,
  getRechargeOrderByNo,
  cancelRechargeOrder,
  repayRechargeOrder,
} from '../../services/wallet';
import './RechargePage.css';

/**
 * 充值页面
 * 职责：展示充值套餐、创建充值订单、查看充值历史
 * 数据：从钱包服务获取套餐列表和订单记录
 * 交互：选择套餐、选择支付方式、创建订单、取消订单、重新支付
 */
const RechargePage = () => {
  const { isMobile } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('WECHAT_NATIVE');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [currentOrderNo, setCurrentOrderNo] = useState('');
  const [paymentPolling, setPaymentPolling] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 加载充值套餐
  const loadPackages = async () => {
    setLoading(true);
    try {
      const res = await getRechargePackages();
      if (res?.data) {
        setPackages(res.data);
        // 默认选中推荐套餐（isHot=true），如果没有推荐套餐则选中第一个
        if (res.data.length > 0 && !selectedPackage) {
          const hotPackage = res.data.find(pkg => pkg.isHot);
          setSelectedPackage(hotPackage || res.data[0]);
        }
      }
    } catch (error) {
      message.error(error.message || '加载套餐失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载订单列表
  const loadOrders = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await getRechargeOrders({ page, pageSize });
      if (res?.data) {
        setOrders(res.data.records || []);
        setPagination({
          current: page,
          pageSize,
          total: res.data.total || 0,
        });
      }
    } catch (error) {
      message.error(error.message || '加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadPackages();
    loadOrders(1, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 选择套餐
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };

  // 选择支付方式
  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  // 确认充值
  const handleConfirmRecharge = () => {
    if (!selectedPackage) {
      message.warning('请选择充值套餐');
      return;
    }
    setConfirmModalVisible(true);
  };

  // 提交充值订单
  const handleSubmitRecharge = async () => {
    setLoading(true);
    try {
      const res = await createRechargeOrder({
        packageId: selectedPackage.id,
        paymentMethod: paymentMethod
      });
      setConfirmModalVisible(false);

      if (res?.data) {
        const { orderNo, paymentUrl, codeUrl, paymentType, status } = res.data;
        const isMockPayment = process.env.REACT_APP_PAYMENT_MOCK_ENABLED === 'true';

        // Native扫码支付：显示二维码
        if (codeUrl && paymentType === 'NATIVE') {
          setCurrentOrderNo(orderNo);
          setQrCodeUrl(codeUrl);
          setQrCodeModalVisible(true);
          // 开始轮询支付结果
          startPaymentPolling(orderNo);
          return;
        }

        // H5支付暂未开通，已从前端移除该选项
        // 如需开通H5支付，请先在微信商户平台申请，并取消下方代码注释
        // if (paymentUrl) {
        //   window.location.href = paymentUrl;
        //   return;
        // }

        // 模拟支付模式：订单已在后端自动标记为已支付
        if (isMockPayment && status === 'SUCCESS') {
          message.success(`支付成功！订单号：${orderNo}`);
          // 刷新订单列表和钱包信息
          loadOrders(pagination.current, pagination.pageSize);
          window.dispatchEvent(new CustomEvent('walletRefresh'));
          return;
        }

        // 生产环境：等待第三方支付回调
        if (!isMockPayment) {
          message.info('订单创建成功，请完成支付...');
        }
      }
    } catch (error) {
      message.error(error.message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  // 开始轮询支付结果
  const startPaymentPolling = (orderNo) => {
    // 清除之前的轮询
    if (paymentPolling) {
      clearInterval(paymentPolling);
    }

    // 每3秒查询一次支付状态
    const timer = setInterval(async () => {
      try {
        const res = await getRechargeOrderByNo(orderNo);
        if (res?.data?.status === 'SUCCESS') {
          clearInterval(timer);
          setPaymentPolling(null);
          setQrCodeModalVisible(false);
          message.success(`支付成功！订单号：${orderNo}`);
          loadOrders(pagination.current, pagination.pageSize);
          window.dispatchEvent(new CustomEvent('walletRefresh'));
        } else if (res?.data?.status === 'CANCELLED' || res?.data?.status === 'FAILED') {
          clearInterval(timer);
          setPaymentPolling(null);
          setQrCodeModalVisible(false);
          message.warning('订单已关闭或支付失败');
          loadOrders(pagination.current, pagination.pageSize);
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
      }
    }, 3000);

    setPaymentPolling(timer);

    // 5分钟后自动停止轮询
    setTimeout(() => {
      if (timer) {
        clearInterval(timer);
        setPaymentPolling(null);
      }
    }, 5 * 60 * 1000);
  };

  // 关闭二维码弹窗
  const handleQrCodeModalClose = () => {
    if (paymentPolling) {
      clearInterval(paymentPolling);
      setPaymentPolling(null);
    }
    setQrCodeModalVisible(false);
    setQrCodeUrl('');
    setCurrentOrderNo('');
    // 刷新订单列表
    loadOrders(pagination.current, pagination.pageSize);
  };

  // 取消订单
  const handleCancelOrder = async (orderNo) => {
    Modal.confirm({
      title: '确认取消订单',
      content: '取消后订单将失效，是否继续？',
      onOk: async () => {
        try {
          await cancelRechargeOrder(orderNo);
          message.success('订单已取消');
          loadOrders(pagination.current, pagination.pageSize);
        } catch (error) {
          message.error(error.message || '取消订单失败');
        }
      },
    });
  };

  // 重新支付
  const handleRepay = async (orderNo) => {
    try {
      const res = await repayRechargeOrder(orderNo);
      const isMockPayment = process.env.REACT_APP_PAYMENT_MOCK_ENABLED === 'true';

      if (res?.data?.codeUrl) {
        // Native扫码支付：显示二维码
        setCurrentOrderNo(orderNo);
        setQrCodeUrl(res.data.codeUrl);
        setQrCodeModalVisible(true);
        // 开始轮询支付结果
        startPaymentPolling(orderNo);
        return;
      }

      // H5支付暂未开通，已从前端移除该选项
      // 如需开通H5支付，请先在微信商户平台申请，并取消下方代码注释
      // if (res?.data?.paymentUrl) {
      //   window.location.href = res.data.paymentUrl;
      //   return;
      // }

      // 模拟支付模式：重新发起支付后，后端会自动标记为已支付
      if (isMockPayment && res?.data?.status === 'SUCCESS') {
        message.success(`支付成功！订单号：${orderNo}`);
        loadOrders(pagination.current, pagination.pageSize);
        window.dispatchEvent(new CustomEvent('walletRefresh'));
        return;
      }

      // 生产环境：等待第三方支付回调
      if (!isMockPayment) {
        message.info('支付请求已发起，请完成支付...');
      }
    } catch (error) {
      message.error(error.message || '发起支付失败');
    }
  };

  // 支付方式渲染
  const renderPaymentMethod = (method) => {
    const methodMap = {
      ALIPAY: { icon: <AlipayOutlined style={{ color: '#1677ff' }} />, text: '支付宝' },
      // 注意：WECHAT 已废弃，统一使用具体支付方式（WECHAT_NATIVE/WECHAT_H5等）
      // 保留 WECHAT 仅用于兼容历史订单数据
      WECHAT: { icon: <WechatOutlined style={{ color: '#52c41a' }} />, text: '微信支付' }, // 历史数据兼容
      WECHAT_NATIVE: { icon: <WechatOutlined style={{ color: '#52c41a' }} />, text: '微信扫码支付' },
      WECHAT_H5: { icon: <WechatOutlined style={{ color: '#52c41a' }} />, text: '微信H5支付' },
      WECHAT_JSAPI: { icon: <WechatOutlined style={{ color: '#52c41a' }} />, text: '微信JSAPI支付' },
      WECHAT_MINI: { icon: <WechatOutlined style={{ color: '#52c41a' }} />, text: '微信小程序支付' },
    };
    const config = methodMap[method] || { icon: <WalletOutlined />, text: method };
    return (
      <span>
        {config.icon} {config.text}
      </span>
    );
  };

  // 订单状态渲染
  const renderOrderStatus = (status) => {
    const statusMap = {
      PENDING: { text: '待支付', color: 'warning' },
      SUCCESS: { text: '已完成', color: 'success' },
      FAILED: { text: '失败', color: 'error' },
      CANCELLED: { text: '已取消', color: 'default' },
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 订单操作列
  const orderColumns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
      ellipsis: true,
    },
    {
      title: '套餐名称',
      dataIndex: 'packageName',
      key: 'packageName',
      width: 120,
    },
    {
      title: '金蝉值',
      dataIndex: 'totalPoints',
      key: 'totalPoints',
      width: 100,
      render: (points) => <span className="points-highlight">{points}</span>,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 80,
      render: (amount) => `¥${amount}`,
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 100,
      render: renderPaymentMethod,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: renderOrderStatus,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        if (record.status === 'PENDING') {
          return (
            <div className="order-actions">
              <Button
                type="link"
                size="small"
                onClick={() => handleRepay(record.orderNo)}
              >
                去支付
              </Button>
              <Button
                type="link"
                size="small"
                danger
                onClick={() => handleCancelOrder(record.orderNo)}
              >
                取消
              </Button>
            </div>
          );
        }
        return null;
      },
    },
  ];

  // 套餐卡片
  const PackageCard = ({ pkg, selected, onClick }) => {
    const savings = pkg.giftPoints > 0
      ? Math.round((pkg.giftPoints / pkg.points) * 100)
      : 0;

    return (
      <div
        className={`package-card ${selected ? 'selected' : ''} ${pkg.isHot ? 'hot' : ''}`}
        onClick={onClick}
      >
        {pkg.isHot && <div className="hot-badge">推荐</div>}
        {savings > 0 && <div className="gift-badge">赠送{pkg.giftPoints}金蝉值</div>}
        <div className="package-header">
          <div className="package-name">{pkg.packageName}</div>
          <div className="package-points">
            <span className="points-value">{pkg.totalPoints}</span>
            <span className="points-unit">金蝉值</span>
          </div>
        </div>
        <div className="package-price">
          <span className="price-symbol">¥</span>
          <span className="price-value">{pkg.price}</span>
        </div>
        {pkg.validDays && (
          <div className="package-validity">有效期：{pkg.validDays}天</div>
        )}
        {selected && (
          <div className="selected-badge">
            <CheckCircleOutlined /> 已选择
          </div>
        )}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'recharge',
      label: '充值中心',
      children: (
        <div className="recharge-content">
          {/* 套餐列表 */}
          <div className="packages-section">
            <div className="section-title">选择充值套餐</div>
            <Row gutter={[16, 16]}>
              {packages.map((pkg) => (
                <Col key={pkg.id} xs={12} sm={8} md={6} lg={4}>
                  <PackageCard
                    pkg={pkg}
                    selected={selectedPackage?.id === pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* 支付方式 */}
          <div className="payment-section">
            <div className="section-title">选择支付方式</div>
            <Radio.Group
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
              className="payment-methods"
            >
              <Radio value="WECHAT_NATIVE" className="payment-option">
                <WechatOutlined className="payment-icon wechat" />
                <span>微信支付（扫码）</span>
              </Radio>
              <Radio value="ALIPAY" className="payment-option" disabled>
                <AlipayOutlined className="payment-icon alipay" />
                <span>支付宝（即将推出）</span>
              </Radio>
            </Radio.Group>
          </div>

          {/* 充值说明 */}
          <Alert
            message="充值说明"
            description={
              <ul className="recharge-notes">
                <li>请使用微信扫描二维码完成支付</li>
                <li>充值成功后金蝉值立即到账</li>
                <li>金蝉值有效期自到账之日起计算</li>
                <li>如有问题请联系客服</li>
              </ul>
            }
            type="info"
            showIcon
            className="recharge-alert"
          />

          {/* 提交按钮 */}
          <div className="submit-section">
            {selectedPackage && (
              <div className="order-summary">
                <span className="summary-label">订单总额：</span>
                <span className="summary-price">¥{selectedPackage.price}</span>
                <span className="summary-points">获得 {selectedPackage.totalPoints} 金蝉值</span>
              </div>
            )}
            <Button
              type="primary"
              size="large"
              icon={<WalletOutlined />}
              onClick={handleConfirmRecharge}
              disabled={!selectedPackage}
              loading={loading}
              block={isMobile}
            >
              立即充值
            </Button>
          </div>
        </div>
      ),
    },
    {
      key: 'orders',
      label: '充值记录',
      children: (
        <Table
          columns={orderColumns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={(newPagination) => loadOrders(newPagination.current, newPagination.pageSize)}
          scroll={{ x: 1000 }}
          size={isMobile ? 'small' : 'middle'}
        />
      ),
    },
  ];

  return (
    <div className="page-container p-recharge-page">
      <div className="page-content">
        <div className="page-title">会员充值</div>

        <Spin spinning={loading && packages.length === 0}>
          <Card bordered={false} className="recharge-card">
            <Tabs items={tabItems} />
          </Card>
        </Spin>

        {/* 确认充值弹窗 */}
        <Modal
          title="确认充值"
          open={confirmModalVisible}
          onOk={handleSubmitRecharge}
          onCancel={() => setConfirmModalVisible(false)}
          confirmLoading={loading}
          okText="确认支付"
          cancelText="取消"
        >
          {selectedPackage && (
            <div className="confirm-recharge-content">
              <div className="confirm-item">
                <span className="confirm-label">充值套餐：</span>
                <span className="confirm-value">{selectedPackage.packageName}</span>
              </div>
              <div className="confirm-item">
                <span className="confirm-label">获得金蝉值：</span>
                <span className="confirm-value points-highlight">{selectedPackage.totalPoints}</span>
              </div>
              {selectedPackage.giftPoints > 0 && (
                <div className="confirm-item">
                  <span className="confirm-label">赠送金蝉值：</span>
                  <span className="confirm-value gift-highlight">
                    <GiftOutlined /> {selectedPackage.giftPoints}
                  </span>
                </div>
              )}
              <div className="confirm-item">
                <span className="confirm-label">支付金额：</span>
                <span className="confirm-value price-highlight">¥{selectedPackage.price}</span>
              </div>
              <div className="confirm-item">
                <span className="confirm-label">支付方式：</span>
                <span className="confirm-value">{renderPaymentMethod(paymentMethod)}</span>
              </div>
              <Divider />
              <div className="confirm-total">
                <span>合计支付：</span>
                <span className="total-price">¥{selectedPackage.price}</span>
              </div>
            </div>
          )}
        </Modal>

        {/* 二维码支付弹窗 */}
        <Modal
          title="微信扫码支付"
          open={qrCodeModalVisible}
          onCancel={handleQrCodeModalClose}
          footer={[
            <Button key="close" onClick={handleQrCodeModalClose}>
              关闭
            </Button>,
          ]}
          width={400}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ marginBottom: '16px' }}>请使用微信扫描下方二维码完成支付</p>
            <div style={{ display: 'inline-block', padding: '10px', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
                alt="支付二维码"
                style={{ width: 200, height: 200 }}
              />
            </div>
            <p style={{ marginTop: '16px', color: '#999' }}>
              订单号：<span style={{ fontFamily: 'monospace' }}>{currentOrderNo}</span>
            </p>
            <p style={{ color: '#52c41a' }}>
              支付完成后将自动刷新，请勿关闭此窗口
            </p>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default RechargePage;
